import axios from 'axios';
import { getToken } from '../utils/tokenStore';

// In dev, route through Vite proxy (/api → backend) to avoid CORS issues.
// In production set VITE_API_BASE_URL to the backend origin.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const API_KEY = import.meta.env.VITE_API_KEY;
// Dev-only logging gate. Production bundles previously logged every API
// response (including PHI: phones, names, diagnoses) and an API-key
// preview to the browser console — fine in dev, leakage on a shared
// kiosk in production.
const IS_DEV = !!import.meta.env.DEV;


if (IS_DEV) {
  // Log configuration on load (dev only)
  console.log('[API Config]', {
    baseURL: API_BASE_URL,
    hasApiKey: !!API_KEY,
  });
}

const api = axios.create({
  baseURL: API_BASE_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY || '',
  },
  timeout: 120000,
});

// Request interceptor — attach JWT and log in dev.
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (IS_DEV) {
      console.log(`[API] ${config.method.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with comprehensive error handling.
api.interceptors.response.use(
  (response) => {
    if (IS_DEV) {
      // Don't dump full response body in production — payloads contain PHI.
      console.log(`[API] ${response.config?.method?.toUpperCase?.() || 'GET'} ${response.config?.url || ''} → ${response.status}`);
    }
    return response;
  },
  (error) => {
    const errorDetails = {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
    };
    console.error('[API] Response error:', errorDetails);

    // Helper: FastAPI validation errors return detail as an array of objects
    const extractDetail = (data) => {
      const detail = data?.detail;
      if (!detail) return null;
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) {
        return detail
          .map((e) => `${e.loc ? e.loc.slice(1).join('.') + ': ' : ''}${e.msg}`)
          .join('; ');
      }
      if (typeof detail === 'object') {
        // Several endpoints answer with a structured detail
        // ({error, message, solutions|problems}) because it carries the one
        // string that says what to fix. String(detail) rendered every one of
        // them as "[object Object]" — precisely the errors worth reading.
        const parts = [];
        if (detail.error) parts.push(detail.error);
        if (detail.message) parts.push(detail.message);
        const extra = detail.solutions || detail.problems;
        if (Array.isArray(extra) && extra.length) parts.push(extra.join(' • '));
        if (parts.length) return parts.join(' — ');
        try {
          return JSON.stringify(detail);
        } catch {
          return 'Unexpected error';
        }
      }
      return String(detail);
    };

    if (error.response?.status === 401) {
      error.userMessage = 'Unauthorized. Please check your API key.';
    } else if (error.response?.status === 403) {
      error.userMessage = 'API authentication failed. Please check your API key.';
    } else if (error.response?.status === 404) {
      error.userMessage = extractDetail(error.response?.data) || 'API endpoint not found. Please check the backend URL.';
    } else if (error.response?.status === 422) {
      error.userMessage = extractDetail(error.response?.data) || 'Validation error. Please check your input.';
    } else if (error.response?.status === 409) {
      // A conflict carries an explanation worth showing verbatim — "this review
      // was already approved; it has not been sent again". Folding it into a
      // generic retry prompt is how a duplicate click looked like a failure.
      error.userMessage = extractDetail(error.response?.data)
        || 'This action conflicts with the current state. Refresh and try again.';
    } else if (error.response?.status === 400) {
      error.userMessage = extractDetail(error.response?.data) || 'Invalid request. Please check your input.';
    } else if (error.response?.status >= 500) {
      error.userMessage = extractDetail(error.response?.data) || 'Server error. Please try again later.';
    } else if (error.code === 'ECONNABORTED') {
      error.userMessage = 'Request timeout. The operation took too long.';
    } else if (error.message === 'Network Error') {
      error.userMessage = 'Cannot connect to backend. Please check the API URL.';
    }

    return Promise.reject(error);
  }
);

// =============================================
// CAMPAIGN APIs
// =============================================

export const campaignAPI = {
  // Smart SMS Campaign with AI content generation
  createSmartSMS: async (data) => {
    const response = await api.post('/campaigns/smart-sms', {
      recipients: data.recipients,
      health_topic: data.health_topic,
      campaign_goals: data.campaign_goals,
      campaign_name: data.campaign_name,
      language: data.language || 'en',
      sender_id: data.sender_id || 'CareArena',
      max_messages_per_recipient: data.max_messages_per_recipient || 3,
      message_delay_minutes: data.message_delay_minutes || 60,
      require_review: data.require_review !== undefined ? data.require_review : true,
      user_id: data.user_id || null,
    });
    return response.data;
  },

  // Direct SMS Campaign – sends a pre-written message to all recipients via /campaigns/sms-direct
  createSMSCampaign: async (data) => {
    const { recipients, message, campaign_name, health_condition, user_id } = data;
    const response = await api.post('/campaigns/sms-direct', {
      campaign_name,
      recipients,
      message,
      health_condition: health_condition || null,
      user_id: user_id || null,
    });
    const result = response.data;
    return {
      success: result.success,
      campaign_name: result.campaign_name,
      sent: result.sent,
      failed: result.failed,
    };
  },

  // Create bulk voice campaign with AI (mNotify + ElevenLabs)
  createVoiceCampaign: async (data) => {
    const response = await api.post('/campaigns/bulk-voice', {
      campaign_name: data.campaign_name,
      recipients: data.recipients,
      disease_name: data.disease_name,
      language: data.language || 'English',
      campaign_goals: data.campaign_goals,
      dry_run: data.dry_run || false,
      require_review: data.require_review !== undefined ? data.require_review : true,
      user_id: data.user_id || null,
    });
    return response.data;
  },

  // Create local language voice campaign (Ghana NLP + mNotify)
  createLocalVoiceCampaign: async (data) => {
    const response = await api.post('/campaigns/local-voice', {
      campaign_name: data.campaign_name,
      recipients: data.recipients,
      disease_name: data.disease_name,
      campaign_goals: data.campaign_goals,
      target_language: data.target_language,
      speaker_id: data.speaker_id,
      is_schedule: data.is_schedule || false,
      schedule_date: data.schedule_date,
      require_review: data.require_review !== undefined ? data.require_review : true,
      user_id: data.user_id || null,
    });
    return response.data;
  },

  // Test voice call with custom message (mNotify + ElevenLabs)
  testVoiceCall: async (data) => {
    const response = await api.post('/test/bulk-voice', {
      recipients: data.recipients,
      message: data.message,
      campaign_name: data.campaign_name,
      require_review: data.require_review !== undefined ? data.require_review : true,
    });
    return response.data;
  },
};

// =============================================
// TEST APIs - Individual Channel Testing
// =============================================

export const testAPI = {
  sendSMS: async (data) => {
    const response = await api.post('/test/send-sms', {
      recipient: data.recipient,
      message: data.message,
    });
    return response.data;
  },

  bulkVoice: async (data) => {
    const response = await api.post('/test/bulk-voice', {
      campaign_name: data.campaign_name,
      recipients: data.recipients,
      message: data.message,
    });
    return response.data;
  },

  makeIVRCall: async (data) => {
    const response = await api.post('/test/make-ivr-call', {
      phone_number: data.phone_number,
      disease_name: data.disease_name,
      patient_name: data.patient_name,
      language: data.language || 'en',
      campaign_goals: data.campaign_goals,
    });
    return response.data;
  },

  sendWhatsApp: async (data) => {
    const response = await api.post('/test/whatsapp', {
      destination: data.destination,
      message: data.message,
    });
    return response.data;
  },
};

// =============================================
// JOB APIs - Campaign Management
// =============================================

export const jobAPI = {
  create: async (data) => {
    const response = await api.post('/jobs/create', {
      disease_name: data.disease_name,
      channel_type: data.channel_type,
      language: data.language,
      patient_database_url: data.patient_database_url,
      supporting_docs: data.supporting_docs || [],
      campaign_metadata: {
        preferred_time: data.preferred_time || '09:00',
        frequency: data.frequency || 'once',
        owner_email: data.owner_email,
        approved: data.approved || false,
        dry_run: data.dry_run || false,
        target_audience: data.target_audience,
        campaign_goals: data.campaign_goals,
        channels: data.channels || [],
      },
      additional_instructions: data.additional_instructions,
    });
    return response.data;
  },

  getStatus: async (jobId) => {
    const response = await api.get(`/jobs/${jobId}/status`);
    return response.data;
  },

  list: async (params = {}) => {
    const response = await api.get('/jobs', {
      params: {
        skip: params.skip || 0,
        limit: params.limit || 20,
      },
    });
    return response.data;
  },

  approve: async (jobId, data) => {
    const response = await api.post(`/admin/approve/${jobId}`, {
      approved: data.approved,
      approved_by: data.approved_by,
      notes: data.notes,
    });
    return response.data;
  },
};

// =============================================
// ELEVENLABS APIs - Conversational AI Agents
// =============================================

export const elevenLabsAPI = {
  createAgent: async (data) => {
    const response = await api.post('/elevenlabs/agents/create', {
      agent_name: data.agent_name,
      first_message: data.first_message,
      system_prompt: data.system_prompt,
      language: data.language || 'en',
    });
    return response.data;
  },

  importPhoneNumber: async (data) => {
    const response = await api.post('/elevenlabs/phone-numbers/import', {
      twilio_phone_number: data.twilio_phone_number,
      agent_id: data.agent_id,
    });
    return response.data;
  },

  makeCall: async (data) => {
    const payload = { to_number: data.phone_number };
    if (data.agent_id) payload.agent_id = data.agent_id;
    if (data.phone_number_id) payload.phone_number_id = data.phone_number_id;
    if (data.health_topic) payload.health_topic = data.health_topic;
    if (data.campaign_goal) payload.campaign_goal = data.campaign_goal;
    if (data.campaign_id) payload.campaign_id = data.campaign_id;
    if (data.user_id) payload.user_id = data.user_id;
    const response = await api.post('/elevenlabs/calls/outbound', payload);
    return response.data;
  },

  makeBulkCalls: async (data) => {
    const response = await api.post('/elevenlabs/calls/bulk-outbound', {
      recipients: data.recipients,
      campaign_name: data.campaign_name,
      health_topic: data.health_topic,
      // Without the goal the agent falls back to its inbound persona as soon
      // as the patient replies — the single-call path has always sent it.
      campaign_goal: data.campaign_goal,
      delay_between_calls: data.delay_between_calls ?? 2,
      agent_id: data.agent_id,
      phone_number_id: data.phone_number_id,
      user_id: data.user_id || null,
    });
    return response.data;
  },

  // Poll a bulk campaign so the console can show what actually happened
  // instead of the optimistic count it posted. Returns a flat shape; the
  // endpoint nests its counters under data._stats.
  getCampaignStatus: async (campaignId) => {
    const response = await api.get(`/campaigns/${campaignId}/status`);
    const data = response.data?.data || {};
    const stats = data._stats || {};
    return {
      status: data.status,
      total: data.total_recipients ?? 0,
      // `sent` is "handed to the provider", NOT "the patient picked up".
      // Named dispatched here so the UI cannot label it as a connect metric.
      dispatched: stats.sent ?? 0,
      failed: stats.failed ?? 0,
      delivered: stats.delivered ?? 0,
      answered: stats.answered_calls ?? 0,
      noAnswer: stats.no_answer ?? 0,
      outcome: data.metadata?.outcome || null,
    };
  },
};

// =============================================
// WHATSAPP APIs
// =============================================

export const whatsappAPI = {
  sendMessage: async (data) => {
    const response = await api.post('/test/whatsapp', {
      destination: data.destination,
      message: data.message,
    });
    return response.data;
  },

  getWebhookInfo: async () => {
    const response = await api.get('/webhooks/whatsapp/setup');
    return response.data;
  },
};

// =============================================
// REVIEW APIs - Human-in-the-Loop
// =============================================

export const reviewAPI = {
  list: async (params = {}, userId = null) => {
    const queryParams = {
      status: params.status,
      content_type: params.content_type,
      priority: params.priority,
      limit: params.limit || 50,
      offset: params.offset || 0,
    };
    if (userId) queryParams.user_id = userId;
    const response = await api.get('/reviews/list', { params: queryParams });
    return response.data;
  },

  getDetail: async (reviewId) => {
    const response = await api.get(`/reviews/${reviewId}`);
    return response.data;
  },

  /** How many items are waiting for a human. Drives the sidebar badge. */
  pendingCount: async (userId = null) => {
    const params = userId ? { user_id: userId } : {};
    const response = await api.get('/reviews/stats', { params });
    const d = response.data || {};
    return d.pending ?? d.pending_reviews ?? d.stats?.pending ?? 0;
  },

  approve: async (reviewId, data) => {
    const response = await api.post(`/reviews/${reviewId}/approve`, {
      review_id: reviewId,
      action: 'approve',
      approved_by: data.approved_by,
      notes: data.notes || null,
      trigger_delivery: data.trigger_delivery !== undefined ? data.trigger_delivery : true,
    });
    return response.data;
  },

  reject: async (reviewId, data) => {
    const formData = new FormData();
    formData.append('rejected_by', data.rejected_by);
    if (data.reason) formData.append('reason', data.reason);
    const response = await api.post(`/reviews/${reviewId}/reject`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Deliver an approved review again after a failed delivery. The banner told
  // reviewers to "fix the cause and resend" while offering no way to resend.
  resend: async (reviewId, data) => {
    const formData = new FormData();
    formData.append('resent_by', data.resent_by);
    const response = await api.post(`/reviews/${reviewId}/resend`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  edit: async (reviewId, data) => {
    const response = await api.post(`/reviews/${reviewId}/edit`, {
      review_id: reviewId,
      edits: data.edits,
      edited_by: data.edited_by,
      auto_approve: data.auto_approve || false,
      edit_notes: data.edit_notes || null,
    });
    return response.data;
  },

  regenerateAudio: async (reviewId) => {
    const response = await api.post(`/reviews/${reviewId}/regenerate-audio`);
    return response.data;
  },

  markInReview: async (reviewId, reviewerEmail) => {
    const formData = new FormData();
    formData.append('reviewer_email', reviewerEmail);
    const response = await api.post(`/reviews/${reviewId}/mark-in-review`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getStats: async (userId = null) => {
    const params = {};
    if (userId) params.user_id = userId;
    const response = await api.get('/reviews/stats', { params });
    return response.data;
  },
};

// =============================================
// ANALYTICS APIs - Real-time Dashboard Data
// =============================================

export const analyticsAPI = {
  // Default sync_elevenlabs=true so every dashboard load reconciles
  // pending AI Voice answer-rate data from the ElevenLabs API. The
  // backend swallows reconcile failures so a slow ElevenLabs call
  // never blocks the dashboard from rendering.
  getSummary: async (syncElevenLabs = true, userId = null) => {
    const params = { sync_elevenlabs: syncElevenLabs };
    if (userId) params.user_id = userId;
    const response = await api.get('/analytics/summary', { params });
    return response.data;
  },

  getWeeklyEngagement: async (weeks = 4, userId = null) => {
    const params = { weeks };
    if (userId) params.user_id = userId;
    const response = await api.get('/analytics/engagement', { params });
    return response.data;
  },

  getChannelPerformance: async (userId = null) => {
    const params = {};
    if (userId) params.user_id = userId;
    const response = await api.get('/analytics/channels', { params });
    return response.data;
  },

  getFeedbackSummary: async (campaignId = null, userId = null) => {
    const params = {};
    if (campaignId) params.campaign_id = campaignId;
    if (userId) params.user_id = userId;
    const response = await api.get('/analytics/feedback', { params });
    return response.data;
  },

  getElevenLabsCallStats: async (agentId = null) => {
    const params = {};
    if (agentId) params.agent_id = agentId;
    const response = await api.get('/analytics/elevenlabs/calls', { params });
    return response.data;
  },

  syncElevenLabsConversations: async (campaignId = null, agentId = null, userId = null) => {
    const params = {};
    if (campaignId) params.campaign_id = campaignId;
    if (agentId) params.agent_id = agentId;
    if (userId) params.user_id = userId;
    const response = await api.post('/analytics/elevenlabs/sync', null, { params });
    return response.data;
  },

  getMNotifyStats: async (userId = null) => {
    const params = {};
    if (userId) params.user_id = userId;
    const response = await api.get('/analytics/mnotify', { params });
    return response.data;
  },
};

// =============================================
// CAMPAIGN MANAGEMENT APIs
// =============================================

export const campaignManagementAPI = {
  getStatus: async (campaignId, userId = null) => {
    const params = {};
    if (userId) params.user_id = userId;
    const response = await api.get(`/campaigns/${campaignId}/status`, { params });
    return response.data;
  },

  pause: async (campaignId, userId = null) => {
    const params = {};
    if (userId) params.user_id = userId;
    const response = await api.post(`/campaigns/${campaignId}/pause`, null, { params });
    return response.data;
  },

  resume: async (campaignId, userId = null) => {
    const params = {};
    if (userId) params.user_id = userId;
    const response = await api.post(`/campaigns/${campaignId}/resume`, null, { params });
    return response.data;
  },
};

// =============================================
// SCHEDULE (Campaign Program) APIs
// =============================================

export const scheduleAPI = {
  create: async (payload) => {
    const response = await api.post('/schedules', payload);
    return response.data;
  },

  list: async (userId = null, status = null) => {
    const params = {};
    if (userId) params.user_id = userId;
    if (status) params.status = status;
    const response = await api.get('/schedules', { params });
    return response.data;
  },

  get: async (programId) => {
    const response = await api.get(`/schedules/${programId}`);
    return response.data;
  },

  /**
   * Who this campaign targeted, what went out, and what arrived.
   *
   * General analytics could only show totals across everything, so nobody
   * could confirm whether a particular campaign had reached a particular
   * person.
   */
  deliveries: async (programId) => {
    const response = await api.get(`/schedules/${programId}/deliveries`);
    return response.data;
  },

  update: async (programId, payload) => {
    const response = await api.put(`/schedules/${programId}`, payload);
    return response.data;
  },

  pause: async (programId) => {
    const response = await api.post(`/schedules/${programId}/pause`);
    return response.data;
  },

  resume: async (programId) => {
    const response = await api.post(`/schedules/${programId}/resume`);
    return response.data;
  },

  cancel: async (programId) => {
    const response = await api.delete(`/schedules/${programId}`);
    return response.data;
  },

  getRuns: async (programId, limit = 20) => {
    const response = await api.get(`/schedules/${programId}/runs`, { params: { limit } });
    return response.data;
  },

  runNow: async (programId) => {
    const response = await api.post(`/schedules/${programId}/run-now`);
    return response.data;
  },

  getMNotifyQueue: async () => {
    const response = await api.get('/schedules/mnotify/messages');
    return response.data;
  },

  updateMNotifyMessage: async (scheduleId, data) => {
    const response = await api.patch(`/schedules/mnotify/messages/${scheduleId}`, {
      sender: data.sender,
      message: data.message,
      scheduled_date: data.scheduled_date,
    });
    return response.data;
  },
};

// =============================================
// FEEDBACK APIs - Patient Feedback Collection
// =============================================

export const feedbackAPI = {
  submit: async (data) => {
    const response = await api.post('/feedback/submit', {
      campaign_id: data.campaign_id,
      phone_number: data.phone_number,
      rating: data.rating,
      feedback_text: data.feedback_text,
    });
    return response.data;
  },
};

// =============================================
// HEALTH & SYSTEM APIs
// =============================================

export const healthAPI = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },

  getInfo: async () => {
    const response = await api.get('/');
    return response.data;
  },
};

// Normalize a phone number to Ghana format without + (233XXXXXXXXX).
// Returns '' for empty / non-digit input rather than the bare prefix '233',
// which previously slipped through into bulk recipient lists as garbage.
// Returns '' for non-Ghana E.164 numbers too — better to reject than to
// silently mangle a UK/US number into a bogus Ghana one.
export const formatPhoneNumber = (phone) => {
  const cleaned = String(phone || '').replace(/\D/g, '');
  if (!cleaned) return '';
  // Already Ghana E.164 digits
  if (cleaned.startsWith('233') && cleaned.length === 12) return cleaned;
  // Local Ghana format (0XXXXXXXXX → 233XXXXXXXXX)
  if (cleaned.startsWith('0') && cleaned.length === 10) return '233' + cleaned.substring(1);
  // 9-digit local without leading 0 (XXXXXXXXX → 233XXXXXXXXX)
  if (cleaned.length === 9) return '233' + cleaned;
  // Anything else is not a Ghana number we can confidently normalise.
  return '';
};

// Normalize a phone number to E.164 format WITH leading + (+233XXXXXXXXX)
// Required by ElevenLabs SIP trunk and mNotify IVR voice outbound calls.
// Returns '' for unparseable input.
export const toE164 = (phone) => {
  const cleaned = formatPhoneNumber(phone);
  return cleaned ? '+' + cleaned : '';
};

// Validate a Ghana phone number
export const isValidGhanaPhone = (phone) => {
  const cleaned = formatPhoneNumber(phone);
  return /^233\d{9}$/.test(cleaned);
};

// Parse and normalize a block of phone numbers — SMS format (no + prefix).
// Drops entries that don't normalise to a valid Ghana number.
export const parsePhoneNumbers = (text) => {
  return String(text)
    .split(/[\n,;]+/)
    .map((num) => num.trim())
    .filter(Boolean)
    .map(formatPhoneNumber)
    .filter(Boolean);
};

// Parse and normalize a block of phone numbers — E.164 format WITH + prefix
// Use this for voice calls (ElevenLabs IVR, mNotify IVR).
// Drops entries that don't normalise to a valid Ghana number.
export const parsePhoneNumbersE164 = (text) => {
  return String(text)
    .split(/[\n,;]+/)
    .map((num) => num.trim())
    .filter(Boolean)
    .map(toE164)
    .filter(Boolean);
};

export default api;
